import {
  ResourceRequestStatus,
  ResourceRequest,
} from '../../hooks/incidentResourceRequestHooks';
import { ApprovalStage } from './ResourceRequestTypes';
import { ResourceSite } from '../../hooks/resourceSiteHooks';

export const getCurrentStage = (
  status: ResourceRequestStatus,
  requestData?: ResourceRequest | null
): ApprovalStage => {
  // First check signatures to determine actual stage regardless of status field
  if (requestData) {
    if (requestData.finance_signature) {
      return 'completed';
    } else if (requestData.logistics_signature) {
      return 'finance';
    } else if (requestData.resl_signature) {
      return 'logistics';
    } else if (requestData.section_chief_signature) {
      return 'resl';
    } else if (requestData.requestor_signature) {
      return 'section_chief';
    } else {
      return 'draft';
    }
  }

  // Fallback to status-based determination
  switch (status) {
    case 'draft':
    case 'partially_signed':
      return 'draft';
    case 'awaiting_approval':
      return 'section_chief';
    case 'awaiting_review':
      return 'resl';
    case 'awaiting_order':
      return 'logistics';
    case 'awaiting_payment':
      return 'finance';
    case 'completed':
      return 'completed';
    default:
      return 'unknown';
  }
};

export const isStageCompleted = (
  stage: ApprovalStage,
  requestData: ResourceRequest | null
): boolean => {
  if (!requestData) return false;

  switch (stage) {
    case 'section_chief':
      return !!requestData.section_chief_signature;
    case 'resl':
      return !!requestData.resl_signature;
    case 'logistics':
      return !!requestData.logistics_signature;
    case 'finance':
      return !!requestData.finance_signature;
    default:
      return false;
  }
};

export const getStageName = (stage: ApprovalStage): string => {
  switch (stage) {
    case 'section_chief':
      return 'Section Chief / Command Approval';
    case 'resl':
      return 'RESL Review';
    case 'logistics':
      return 'Logistics Section';
    case 'finance':
      return 'Finance Section';
    default:
      return '';
  }
};

export const getStageIcon = (stage: ApprovalStage): string => {
  switch (stage) {
    case 'section_chief':
      return '👤';
    case 'resl':
      return '📋';
    case 'logistics':
      return '🚚';
    case 'finance':
      return '💰';
    default:
      return '';
  }
};

// Priority options for resource requests
export const priorityOptions = [
  { value: 'Routine', label: 'Routine' },
  { value: 'Urgent', label: 'Urgent' },
];

// Check if request is completed (either by status or by having finance signature)
export const isRequestCompleted = (
  requestData: ResourceRequest | null | undefined
): boolean => {
  if (!requestData) return false;
  return (
    requestData.status === 'completed' ||
    (requestData.status === 'awaiting_payment' &&
      !!requestData.finance_signature)
  );
};

// Get human-readable location display from location ID or geolocation
export const getLocationDisplay = (
  locationId?: string,
  geolocation?: { address?: string; lat?: number; lng?: number },
  resourceSites?: ResourceSite[]
): string => {
  if (locationId && resourceSites) {
    const site = resourceSites.find((s) => s.id === locationId);
    return site?.name || 'Unknown Site';
  }
  if (geolocation?.address) {
    return geolocation.address;
  }
  return 'Not specified';
};
